// src/components/AGTFit/workout/RoutineManager.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  alpha,
  Paper
} from '@mui/material';
import {
  Edit,
  Delete,
  Save,
  X,
  Plus,
  Dumbbell,
  Play
} from 'lucide-react';
import agtfitService from '../../service/agtfitService';
import ExerciseExplorer from '../explorer/ExerciseExplorer';

// Removed unused imports: motion, AnimatePresence, itemVariants

// Constantes de estilo
const FIT_ORANGE = '#FF6B00';
const GRADIENT = 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

const RoutineManager = ({ open, onClose, selectedRoutine = null, onStartWorkout }) => {
  const theme = useTheme();
  
  // Estados para manejar la rutina
  const [routine, setRoutine] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [routineName, setRoutineName] = useState('');
  const [routineDescription, setRoutineDescription] = useState('');
  const [exercises, setExercises] = useState([]);
  
  // Estado para el diálogo de explorador de ejercicios
  const [openExerciseExplorer, setOpenExerciseExplorer] = useState(false);
  
  // Estados para manejar carga y errores
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Cargar datos de la rutina si se proporciona un ID
  useEffect(() => {
    if (selectedRoutine && selectedRoutine.routineId) {
      loadRoutineDetails(selectedRoutine.routineId);
    } else if (selectedRoutine) {
      // Si tenemos los datos completos de la rutina
      setRoutine(selectedRoutine);
      setRoutineName(selectedRoutine.name);
      setRoutineDescription(selectedRoutine.description || '');
      setExercises(selectedRoutine.exercises || []);
      setIsEditing(false);
    }
  }, [selectedRoutine]);
  
  // Función para cargar los detalles de una rutina
  const loadRoutineDetails = async (routineId) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await agtfitService.getRoutine(routineId);
      
      if (response.success && response.data) {
        setRoutine(response.data);
        setRoutineName(response.data.name);
        setRoutineDescription(response.data.description || '');
        setExercises(response.data.exercises || []);
      } else {
        setError('No se pudo cargar la rutina');
      }
    } catch (err) {
      setError('Error al cargar la rutina: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Función para guardar la rutina
  const handleSaveRoutine = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      if (!routineName.trim()) {
        setError('El nombre de la rutina es obligatorio');
        setLoading(false);
        return;
      }
      
      if (!exercises.length) {
        setError('Debes añadir al menos un ejercicio');
        setLoading(false);
        return;
      }
      
      const routineData = {
        name: routineName,
        description: routineDescription,
        exercises: exercises
      };
      
      let response;
      
      if (routine && routine.routineId) {
        // Actualizar rutina existente
        response = await agtfitService.updateRoutine(routine.routineId, routineData);
      } else {
        // Crear nueva rutina
        response = await agtfitService.createRoutine(routineData);
      }
      
      if (response.success) {
        setSuccess(routine ? 'Rutina actualizada correctamente' : 'Rutina creada correctamente');
        setRoutine(response.data);
        setIsEditing(false);
        
        // Notificar al componente padre para actualizar la lista
        setTimeout(() => {
          onClose(true, response.data);
        }, 1500);
      } else {
        setError(response.error || 'Error al guardar la rutina');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Función para eliminar la rutina
  const handleDeleteRoutine = async () => {
    if (!routine || !routine.routineId) return;
    
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta rutina?')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await agtfitService.deleteRoutine(routine.routineId);
      
      if (response.success) {
        setSuccess('Rutina eliminada correctamente');
        
        // Notificar al componente padre y cerrar después de un breve retraso
        setTimeout(() => {
          onClose(true);
        }, 1500);
      } else {
        setError(response.error || 'Error al eliminar la rutina');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Función para añadir un ejercicio desde el explorador
  const handleAddExercise = (exercise) => {
    setOpenExerciseExplorer(false);
    
    // Convertir el ejercicio al formato necesario para la rutina
    const newExercise = {
      id: exercise.id,
      name: exercise.name,
      muscle: exercise.muscle || exercise.primaryMuscles?.[0] || '',
      sets: 3, // Valores predeterminados
      reps: 12,
      weight: 0,
      notes: ''
    };
    
    setExercises([...exercises, newExercise]);
  };
  
  // Función para eliminar un ejercicio de la rutina
  const handleRemoveExercise = (index) => {
    const updatedExercises = [...exercises];
    updatedExercises.splice(index, 1);
    setExercises(updatedExercises);
  };
  
  // Función para actualizar un ejercicio
  const handleUpdateExercise = (index, field, value) => {
    const updatedExercises = [...exercises];
    updatedExercises[index] = {
      ...updatedExercises[index],
      [field]: value
    };
    setExercises(updatedExercises);
  };
  
  // Función para iniciar la rutina como entrenamiento
  const handleStartWorkout = () => {
    if (onStartWorkout && routine) {
      onStartWorkout(routine);
      onClose(false); // Cerrar el gestor de rutinas
    }
  };

  // Estilos para los botones principales
  const primaryButtonStyle = {
    borderRadius: '20px',
    textTransform: 'none',
    py: 1,
    px: 3,
    fontWeight: 500,
    background: GRADIENT,
    color: '#FFFFFF',
    boxShadow: 'none',
    '&:hover': {
      background: GRADIENT,
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
    }
  };

  // Estilos para los botones secundarios
  const secondaryButtonStyle = {
    borderRadius: '20px',
    textTransform: 'none',
    py: 1,
    px: 3,
    fontWeight: 500,
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bgLight
      : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'),
    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : theme.palette.text.primary,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.actualMode === 'wallpaper'
        ? 'rgba(128, 128, 128, 0.4)'
        : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'),
    }
  };

  // Estilo para botón de eliminar
  const deleteButtonStyle = {
    borderRadius: '20px',
    textTransform: 'none',
    py: 1,
    px: 3,
    fontWeight: 500,
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(255,59,48,0.2)'
      : (theme.palette.mode === 'dark' ? 'rgba(255,59,48,0.15)' : '#FFEFEF'),
    color: '#FF3B30',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.actualMode === 'wallpaper'
        ? 'rgba(255,59,48,0.3)'
        : (theme.palette.mode === 'dark' ? 'rgba(255,59,48,0.25)' : '#FFE0E0'),
    }
  };
  
  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose(false)}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          overflow: 'hidden',
          boxShadow: theme.palette.actualMode === 'wallpaper'
            ? 'none'
            : (theme.palette.mode === 'dark'
              ? '0 20px 60px rgba(0, 0, 0, 0.5)'
              : '0 20px 60px rgba(0, 0, 0, 0.2)'),
          background: theme.palette.actualMode === 'wallpaper'
            ? WALLPAPER_COLORS.bg
            : (theme.palette.mode === 'dark'
              ? 'rgba(30, 30, 32, 0.95)'
              : 'rgba(255, 255, 255, 1)'),
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
        }
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          background: theme.palette.actualMode === 'wallpaper'
            ? 'linear-gradient(180deg, rgba(255,107,0,0.15) 0%, rgba(0,0,0,0) 100%)'
            : (theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, rgba(255,107,0,0.15) 0%, rgba(0,0,0,0) 100%)'
              : 'linear-gradient(180deg, rgba(255,107,0,0.05) 0%, rgba(255,255,255,0) 100%)'),
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            px: 3,
            py: 2
          }}
        >
          {isEditing ? (
            <TextField
              fullWidth
              value={routineName}
              onChange={(e) => setRoutineName(e.target.value)}
              placeholder="Nombre de la rutina"
              variant="standard"
              sx={{ 
                fontSize: '1.25rem', 
                fontWeight: 600,
                '& .MuiInput-underline:before': {
                  borderBottomColor: alpha(FIT_ORANGE, 0.3)
                },
                '& .MuiInput-underline:after': {
                  borderBottomColor: FIT_ORANGE
                }
              }}
              InputProps={{
                sx: {
                  fontSize: '1.5rem',
                  fontWeight: 700
                }
              }}
            />
          ) : (
            <Typography 
              variant="h5" 
              fontWeight={700}
              sx={{ 
                background: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {routine ? routine.name : 'Nueva Rutina'}
            </Typography>
          )}
          
          <IconButton
            onClick={() => !loading && onClose(false)}
            sx={{
              backgroundColor: theme.palette.actualMode === 'wallpaper'
                ? WALLPAPER_COLORS.bgLight
                : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'),
              color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit',
              '&:hover': {
                backgroundColor: theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(128, 128, 128, 0.4)'
                  : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'),
              }
            }}
          >
            <X size={18} />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent
        sx={{
          p: 3,
          backgroundColor: theme.palette.actualMode === 'wallpaper'
            ? 'transparent'
            : theme.palette.background.default
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: FIT_ORANGE }} />
          </Box>
        ) : (
          <>
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2
                }}
              >
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2
                }}
              >
                {success}
              </Alert>
            )}
            
            {isEditing ? (
              <TextField
                fullWidth
                multiline
                rows={2}
                value={routineDescription}
                onChange={(e) => setRoutineDescription(e.target.value)}
                placeholder="Descripción (opcional)"
                variant="outlined"
                size="small"
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: theme.palette.actualMode === 'wallpaper'
                      ? WALLPAPER_COLORS.bgLight
                      : (theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : '#F5F5F7'),
                    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit',
                    '& fieldset': {
                      borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : 'transparent'
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : theme.palette.divider
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: FIT_ORANGE,
                      borderWidth: 1
                    }
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'inherit',
                    opacity: 1
                  }
                }}
              />
            ) : routine?.description ? (
              <Typography
                variant="body1"
                sx={{
                  mb: 3,
                  px: 1,
                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'text.secondary'
                }}
              >
                {routine.description}
              </Typography>
            ) : null}
            
            <Box 
              sx={{ 
                mb: 2, 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center' 
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
                }}
              >
                <Dumbbell size={18} color={FIT_ORANGE} />
                Ejercicios
              </Typography>
              
              {isEditing && (
                <Button
                  startIcon={<Plus size={16} />}
                  onClick={() => setOpenExerciseExplorer(true)}
                  sx={{
                    ...secondaryButtonStyle,
                    color: FIT_ORANGE,
                    backgroundColor: theme.palette.actualMode === 'wallpaper'
                      ? WALLPAPER_COLORS.bgLight
                      : secondaryButtonStyle.backgroundColor,
                    '&:hover': {
                      backgroundColor: theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(128, 128, 128, 0.4)'
                        : secondaryButtonStyle['&:hover'].backgroundColor,
                    }
                  }}
                >
                  Añadir ejercicio
                </Button>
              )}
            </Box>
            
            {exercises.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  bgcolor: theme.palette.actualMode === 'wallpaper'
                    ? WALLPAPER_COLORS.bgLight
                    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#F8F8FA'),
                  border: '1px dashed',
                  borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : theme.palette.divider,
                  borderRadius: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Box 
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,107,0,0.1)' : 'rgba(255,107,0,0.05)',
                    mb: 2
                  }}
                >
                  <Dumbbell size={28} color={FIT_ORANGE} />
                </Box>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{
                    mb: 1,
                    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit'
                  }}
                >
                  Sin ejercicios
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 3,
                    color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'text.secondary'
                  }}
                >
                  Esta rutina aún no tiene ejercicios asignados
                </Typography>
                {isEditing && (
                  <Button 
                    variant="contained" 
                    startIcon={<Plus size={16} />}
                    onClick={() => setOpenExerciseExplorer(true)}
                    sx={primaryButtonStyle}
                  >
                    Añadir ejercicio
                  </Button>
                )}
              </Paper>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  bgcolor: theme.palette.actualMode === 'wallpaper'
                    ? WALLPAPER_COLORS.bgLight
                    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#F8F8FA'),
                  borderRadius: 3,
                  overflow: 'hidden',
                  mb: 2,
                  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none'
                }}
              >
                <List sx={{ p: 0 }}>
                  {exercises.map((exercise, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && <Divider sx={{ opacity: 0.5 }} />}
                      <ListItem 
                        sx={{ 
                          py: 1.5,
                          px: 2.5,
                          flexDirection: isEditing ? 'column' : 'row',
                          alignItems: isEditing ? 'flex-start' : 'center',
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)'
                          }
                        }}
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          width: '100%',
                          mb: isEditing ? 1.5 : 0,
                          justifyContent: 'space-between'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box 
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: GRADIENT,
                                mr: 2,
                                flexShrink: 0
                              }}
                            >
                              <Dumbbell size={20} color="#FFFFFF" />
                            </Box>
                            
                            <ListItemText
                              primary={exercise.name}
                              secondary={exercise.muscle}
                              primaryTypographyProps={{
                                fontWeight: 600,
                                fontSize: '0.95rem'
                              }}
                              secondaryTypographyProps={{
                                fontSize: '0.8rem'
                              }}
                            />
                          </Box>
                          
                          {isEditing ? (
                            <IconButton 
                              edge="end" 
                              size="small" 
                              onClick={() => handleRemoveExercise(index)}
                              sx={{
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,59,48,0.15)' : '#FFEFEF',
                                color: '#FF3B30',
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,59,48,0.25)' : '#FFE0E0',
                                }
                              }}
                            >
                              <Delete size={16} />
                            </IconButton>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Chip 
                                size="small" 
                                label={`${exercise.sets} series`} 
                                sx={{ 
                                  fontSize: '0.75rem',
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,107,0,0.15)' : 'rgba(255,107,0,0.08)',
                                  color: FIT_ORANGE,
                                  fontWeight: 500,
                                  borderRadius: '8px'
                                }}
                              />
                              <Chip 
                                size="small" 
                                label={`${exercise.reps} reps`} 
                                sx={{ 
                                  fontSize: '0.75rem',
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,107,0,0.15)' : 'rgba(255,107,0,0.08)',
                                  color: FIT_ORANGE,
                                  fontWeight: 500,
                                  borderRadius: '8px'
                                }}
                              />
                              {exercise.weight > 0 && (
                                <Chip 
                                  size="small" 
                                  label={`${exercise.weight} kg`} 
                                  sx={{ 
                                    fontSize: '0.75rem',
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,107,0,0.15)' : 'rgba(255,107,0,0.08)',
                                    color: FIT_ORANGE,
                                    fontWeight: 500,
                                    borderRadius: '8px'
                                  }}
                                />
                              )}
                            </Box>
                          )}
                        </Box>
                        
                        {isEditing && (
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            width: '100%',
                            alignItems: 'center',
                            flexWrap: 'wrap'
                          }}>
                            <TextField
                              label="Series"
                              type="number"
                              size="small"
                              value={exercise.sets}
                              onChange={(e) => handleUpdateExercise(index, 'sets', parseInt(e.target.value) || 0)}
                              InputProps={{
                                inputProps: { min: 1 },
                                sx: { borderRadius: 2 }
                              }}
                              sx={{
                                width: 80,
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                                    ? WALLPAPER_COLORS.bgLight
                                    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#F5F5F7'),
                                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit',
                                  '& fieldset': {
                                    borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : 'transparent'
                                  },
                                  '&:hover fieldset': {
                                    borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : theme.palette.divider
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: FIT_ORANGE,
                                    borderWidth: 1
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'inherit'
                                }
                              }}
                            />
                            <TextField
                              label="Reps"
                              type="number"
                              size="small"
                              value={exercise.reps}
                              onChange={(e) => handleUpdateExercise(index, 'reps', parseInt(e.target.value) || 0)}
                              InputProps={{
                                inputProps: { min: 1 },
                                sx: { borderRadius: 2 }
                              }}
                              sx={{
                                width: 80,
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                                    ? WALLPAPER_COLORS.bgLight
                                    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#F5F5F7'),
                                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit',
                                  '& fieldset': {
                                    borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : 'transparent'
                                  },
                                  '&:hover fieldset': {
                                    borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : theme.palette.divider
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: FIT_ORANGE,
                                    borderWidth: 1
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'inherit'
                                }
                              }}
                            />
                            <TextField
                              label="Peso (kg)"
                              type="number"
                              size="small"
                              value={exercise.weight || 0}
                              onChange={(e) => handleUpdateExercise(index, 'weight', parseFloat(e.target.value) || 0)}
                              InputProps={{
                                inputProps: { min: 0, step: 0.5 },
                                sx: { borderRadius: 2 }
                              }}
                              sx={{
                                width: 100,
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                                    ? WALLPAPER_COLORS.bgLight
                                    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#F5F5F7'),
                                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit',
                                  '& fieldset': {
                                    borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : 'transparent'
                                  },
                                  '&:hover fieldset': {
                                    borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : theme.palette.divider
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: FIT_ORANGE,
                                    borderWidth: 1
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'inherit'
                                }
                              }}
                            />
                            <TextField
                              label="Notas"
                              size="small"
                              value={exercise.notes || ''}
                              onChange={(e) => handleUpdateExercise(index, 'notes', e.target.value)}
                              fullWidth
                              InputProps={{
                                sx: { borderRadius: 2 }
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                                    ? WALLPAPER_COLORS.bgLight
                                    : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#F5F5F7'),
                                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.text : 'inherit',
                                  '& fieldset': {
                                    borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : 'transparent'
                                  },
                                  '&:hover fieldset': {
                                    borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : theme.palette.divider
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: FIT_ORANGE,
                                    borderWidth: 1
                                  }
                                },
                                '& .MuiInputLabel-root': {
                                  color: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.textSecondary : 'inherit'
                                }
                              }}
                            />
                          </Box>
                        )}
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}
          </>
        )}
        
        {/* Explorador de ejercicios */}
        <ExerciseExplorer
          open={openExerciseExplorer}
          onClose={() => setOpenExerciseExplorer(false)}
          onSelectExercise={handleAddExercise}
        />
      </DialogContent>
      
      <DialogActions
        sx={{
          p: 3,
          justifyContent: 'space-between',
          background: theme.palette.actualMode === 'wallpaper'
            ? 'linear-gradient(0deg, rgba(255,107,0,0.1) 0%, rgba(0,0,0,0) 100%)'
            : (theme.palette.mode === 'dark'
              ? 'linear-gradient(0deg, rgba(255,107,0,0.1) 0%, rgba(0,0,0,0) 100%)'
              : 'linear-gradient(0deg, rgba(255,107,0,0.03) 0%, rgba(255,255,255,0) 100%)'),
        }}
      >
        <Box>
          {routine && !isEditing && (
            <Button
              variant="text"
              color="error"
              startIcon={<Delete size={16} />}
              onClick={handleDeleteRoutine}
              disabled={loading}
              sx={deleteButtonStyle}
            >
              Eliminar
            </Button>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isEditing ? (
            <>
              <Button
                variant="text"
                onClick={() => {
                  // Restaurar valores originales
                  if (routine) {
                    setRoutineName(routine.name);
                    setRoutineDescription(routine.description || '');
                    setExercises(routine.exercises || []);
                  }
                  setIsEditing(false);
                }}
                disabled={loading}
                sx={secondaryButtonStyle}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                startIcon={<Save size={16} />}
                onClick={handleSaveRoutine}
                disabled={loading}
                sx={primaryButtonStyle}
              >
                Guardar
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="text"
                startIcon={<Edit size={16} />}
                onClick={() => setIsEditing(true)}
                disabled={loading}
                sx={secondaryButtonStyle}
              >
                Editar
              </Button>
              {routine && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Play size={16} />}
                  onClick={handleStartWorkout}
                  disabled={loading}
                  sx={primaryButtonStyle}
                >
                  Iniciar
                </Button>
              )}
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default RoutineManager;